import { Link } from '@tanstack/react-router'

import { UserIcon } from '@/components/icons'
import { accountName } from '@/features/back-office/account-name'
import { HeaderMenu } from '@/features/nav/menu'
import { authPath, request } from '@/lib/api'
import { type System, type UserOf, clearAuth } from '@/lib/auth-store'
import { MY_ORDERS_PATH, MY_RESTAURANTS_PATH, profilePathFor } from '@/lib/return-path'
import { useT } from '@/lib/i18n/translate'

const ROW = 'hover:bg-accent-soft flex min-h-tap w-full items-center rounded-lg px-3 text-left'

/** The account pages and logout, the same on every header (2026-09-12 header/nav decision). */
export function AvatarMenu<S extends System>({ system, user }: { system: S; user: UserOf<S> }) {
  const t = useT()

  async function logout() {
    try {
      await request(authPath(system, '/logout'), { method: 'POST' })
    } catch {
      // A failed call is fine — clearing local auth below drops to login page (D1 rule).
    }
    clearAuth(system)
  }

  return (
    <HeaderMenu label={t('nav.account')} icon={<UserIcon />} panel="right-0 w-64">
      {(close) => (
        <>
          <p className="text-muted truncate px-3 py-2 text-sm">{accountName(user)}</p>
          <Link to={profilePathFor(system)} onClick={close} className={ROW}>
            {t('profile.title')}
          </Link>
          {system === 'restaurant' && (
            <>
              <Link to={MY_ORDERS_PATH} search={{ page: 1 }} onClick={close} className={ROW}>
                {t('myOrders.title')}
              </Link>
              <Link to={MY_RESTAURANTS_PATH} onClick={close} className={ROW}>
                {t('nav.myRestaurants')}
              </Link>
            </>
          )}
          <div className="border-border my-1 border-t" />
          <button
            type="button"
            onClick={() => {
              close()
              void logout()
            }}
            className={ROW}
          >
            {t('auth.logout')}
          </button>
        </>
      )}
    </HeaderMenu>
  )
}
